/**
 * 「帳款明細表」的品項層級版面(REQ-20260831002)。
 *
 * 原本這張表是「一列一張未結單據」(客戶 / 業務員 / 到期日 / 含稅總計 / 已沖 / 折讓 / 未結)。
 * 使用者要在**同一張表上**同時看到品項與欠款,所以表身改成
 * 日期 / 單號 / 商品名稱 / 數量 / 單位 / 單價 / 金額 / 折讓 / 已收款 / 未收款。
 *
 * 兩個要點:
 *  - **一列一品項**,同一張單第二列起日期 / 單號 / 折讓 / 已收款 / 未收款留白 ——
 *    後三者是**單頭**金額,印在每一列會讓一張三品項的單看起來欠了三倍的錢
 *    (REQ-20260831001 §一.A 的同一條)。
 *  - **每一欄都關掉排序**:一列不再等於一張單,排序會把同一張單的品項打散。
 *
 * 對齊(REQ-20260831009):日期 / 單號 / 單位 / 單價 / 金額 / 折讓 / 已收款置中,
 * 商品名稱靠左(齊頭)、數量與未收款靠右(齊尾)。四個金額欄改置中後會掉掉
 * `ReportTable` 綁在 `align: 'right'` 上的 `font-mono`,所以各自補 `className: 'font-mono'`
 * —— 字型換掉會改變欄寬,而欄寬直接決定列印能不能不縮放就塞進 A4。
 *
 * 字型 / 字級(REQ-20260901003):日期補上 `font-mono` —— 十欄的 td 字級本來就一致
 * (都是表身的 `text-sm`),日期是唯一還用比例字型的數字欄。商品名稱與單位改
 * `text-xs`(中文小一級)。日期同時拆成「年 / 月-日」上下兩列:目的是**先把列高墊高**,
 * 品名折成兩行時版面才不會忽高忽低。列印版面另外把剩餘寬度全給商品名稱
 * (`growColumnKey`),日期收窄省下的位置才不會被十欄平分掉。
 *
 * 列印(REQ-20260901002):**畫面與紙上分家了**。螢幕仍是這張 `ReportTable`,
 * 紙上換成 `ArDetailPrintPages` —— 它自己把品項列裝箱成固定張數的 A4 區塊,
 * 每頁重印抬頭與表頭列、合計只印最後一頁、頁數印得出分母。兩份吃同一組
 * columns / items / footer(都從這裡傳下去),沒有第二份欄位定義。
 * 未勾「依客戶分別列印」時 `ReportsPanel` 才會傳 `printHeader`。
 *
 * 交貨認列(REQ-20260901008):請款認的是**完貨交貨日**,所以這張表拆成上下兩區 ——
 * 上區「已交貨(列入請款)」是原本的十欄,日期欄改印完貨日;下區「未交貨」用同一組
 * 欄位定義(只多一個標籤),整區 `print:hidden`。合計與列印一律只吃上區。
 *
 * 合計說明區(REQ-20260901012):表格底下多一塊有標籤的 `ArDetailTotalsSummary`
 * (數量合計 / 未稅 / 營業稅 / 合計 / 折讓 / 已收款 / 應收總計)。
 * `<tfoot>` 那排數字**保留** —— 它落在各自欄位底下,說明區是補充不是取代。
 * 紙上那份透過 `ArDetailPrintPages` 的 `summary` prop 只印在最後一頁。
 *
 * 三態 / 合計列 / 表格樣式仍走共用的 `ReportTable`(ui-guidelines §1 / §4),
 * 不在這裡另寫一份。`ar-by-person`(業務員應收帳款明細)與 `ap-detail` 維持原本的
 * `DetailTable`,不受這張表影響。
 */
import type { ArApDetailReport, ArApDetailRow, PartyInfo } from '@app/shared';
import { formatMoney } from '../../lib/format-money';
import { ArDetailPrintPages } from './ArDetailPrintPages';
import { ArDetailTotalsSummary } from './ArDetailTotalsSummary';
import { formatQty, printLines, type PrintLine } from './print-lines';
import { ReportTable, type ReportColumn } from './ReportTable';

/** 攤平後的一列:哪張單、哪個品項、是不是該單的第一列 */
export interface ItemRow {
  row: ArApDetailRow;
  line: PrintLine;
  isFirst: boolean;
}

/** 單頭欄位只印在該單第一列,其餘留白 */
const head = (r: ItemRow, text: string): string => (r.isFirst ? text : '');

/**
 * 日期欄印的是**完貨交貨日**(REQ-20260901008)—— 請款認列的是貨交到客人手上那天,
 * 不是下單那天。`deliveryDate` 為 null 有兩種情況,兩種都退回訂單日期:
 *  - 尚未交貨(未交貨區的每一列)
 *  - 出貨紀錄上線(2026-08-15)前、沒有任何出貨紀錄的舊單 —— 視為已交貨但沒有可信的交貨日
 *
 * **欄位標題仍寫「日期」**:改成「完貨日期」會撐寬這一欄,而十欄的寬度是量過才剛好
 * 不需要縮放就塞進 A4 的(REQ-20260901002 / REQ-20260901003)。要讓使用者知道這張表
 * 認的是交貨日,說明放在 `ReportsPanel` 的起訖日旁邊,不放在會改變版面的地方。
 */
const displayDate = (r: ItemRow): string => r.row.deliveryDate ?? r.row.txnDate;

/** 分組版面(`GroupedArDetailStatements`)也吃這一句,所以 export —— 不複製第二份文案 */
export const EMPTY_HINT =
  '這個區間內沒有已完貨交貨的未結單據。請款認列以完貨交貨日為準,到「出貨管理」把單出完後才會列入。';

/**
 * 十欄的定義。**放在模組層而不是元件裡**(REQ-20260901002):列印版面
 * (`ArDetailPrintPages`)吃同一份欄位,而它要拿這份陣列當離屏量測的觸發條件 ——
 * 每次 render 產生新陣列會讓量測無限重跑。這裡沒有任何值依賴 props,
 * 提到模組層就是一個固定參照,順帶也不用每次 render 重建十個物件。
 */
export const arDetailColumns: Array<ReportColumn<ItemRow>> = [
  {
    key: 'txnDate',
    label: '日期',
    align: 'center',
    sortable: false,
    // 日期原本是唯一用比例字型的數字欄,並排時看起來就跟單號不一樣大(REQ-20260901003 第 2 點)。
    // 字級本來就一致(十欄的 td 都是 14px),差的只有字型。
    className: 'font-mono',
    // 排序與 fallback 仍要完整的 YYYY-MM-DD,所以 value 不動,只換 render
    value: (r) => head(r, displayDate(r)),
    /*
     * 拆成「年 / 月-日」上下兩列(REQ-20260901003 第 4 點)。目的不是美觀 ——
     * 品名長到折兩行時列高會忽高忽低,先把日期欄墊成兩行可以把第一列的高度統一。
     * `whitespace-nowrap`:`02-04` 可以在連字號後斷行,不擋住就會折成第三行。
     * **純字串切割,不碰 `new Date()`** —— 容器跑 UTC(CLAUDE.md §6.1),
     * 而 `txnDate` 後端就是 `YYYY-MM-DD`(`toYmd()`),轉成 Date 只會差一天。
     */
    render: (r) => {
      const ymd = head(r, displayDate(r));
      if (!ymd) return '';
      return (
        <>
          <span className="block whitespace-nowrap">{ymd.slice(0, 4)}</span>
          <span className="block whitespace-nowrap">{ymd.slice(5)}</span>
        </>
      );
    },
  },
  {
    key: 'orderNo',
    label: '單號',
    align: 'center',
    sortable: false,
    className: 'font-mono',
    value: (r) => head(r, r.row.orderNo),
  },
  {
    key: 'productName',
    label: '商品名稱',
    sortable: false,
    // 中文欄位比表身小一級(REQ-20260901003 第 3 點)。**只能用 `text-xs`**,
    // 不可以寫 `text-[Nrem]`:任意字級不會 emit line-height,中文折行會出事。
    className: 'text-xs',
    value: (r) => r.line.productName ?? '',
    // 品名不加 whitespace-nowrap:長品名要能自己換行,不要把表格撐出紙外
    render: (r) =>
      r.line.productName ?? <span className="text-slate-500">（無品項明細）</span>,
  },
  {
    key: 'qty',
    label: '數量',
    align: 'right',
    sortable: false,
    // 數量是 DECIMAL(14,4),不能套 formatMoney(那支會四捨五入成整數)
    value: (r) => (r.line.qty == null ? '' : formatQty(r.line.qty)),
  },
  {
    key: 'unitName',
    label: '單位',
    align: 'center',
    sortable: false,
    // 同商品名稱:中文欄位小一級(REQ-20260901003 第 3 點)
    className: 'text-xs',
    value: (r) => r.line.unitName ?? '',
  },
  {
    key: 'unitPrice',
    label: '單價',
    align: 'center',
    sortable: false,
    className: 'font-mono',
    // 單價原樣印:四捨五入會讓「數量 × 單價」對不上金額
    value: (r) => r.line.unitPrice ?? '',
  },
  {
    key: 'amount',
    label: '金額',
    align: 'center',
    sortable: false,
    className: 'font-mono',
    value: (r) => formatMoney(r.line.amount),
  },
  {
    key: 'allowanceAmount',
    label: '折讓',
    align: 'center',
    sortable: false,
    className: 'font-mono',
    // 折讓沒填時印 0 不印「—」:請款文件上的破折號看起來像欄位壞掉
    value: (r) => head(r, formatMoney(r.row.allowanceAmount ?? 0)),
  },
  {
    key: 'settledAmount',
    label: '已收款',
    align: 'center',
    sortable: false,
    className: 'font-mono',
    value: (r) => head(r, formatMoney(r.row.settledAmount)),
  },
  {
    key: 'outstanding',
    label: '未收款',
    align: 'right',
    sortable: false,
    value: (r) => head(r, formatMoney(r.row.outstanding)),
  },
];

/**
 * 未交貨區的欄位(REQ-20260901008)。**沿用上面同一組定義**,只把單號那一欄換成
 * 「單號 + 未交貨標籤」的 render —— 需求要的是「分開顯示並標註」,兩區的欄位一旦
 * 各寫一份就會慢慢分岔。
 *
 * 一樣提到模組層:`ReportTable` 的排序 `useMemo` 把 columns 列進依賴,
 * 每次 render 產生新陣列會讓它白跑一遍。
 */
const undeliveredColumns: Array<ReportColumn<ItemRow>> = arDetailColumns.map((c) =>
  c.key === 'orderNo'
    ? {
        ...c,
        // 標籤跟著單號走,所以只出現在該單第一列 —— 與其他單頭欄位同一條規則
        render: (r) =>
          r.isFirst ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              {r.row.orderNo}
              <span className="rounded bg-amber-100 px-1 py-px text-[10px] text-amber-700 font-sans">
                未交貨
              </span>
            </span>
          ) : (
            ''
          ),
      }
    : c,
);

/** 抬頭要的四項,由 `ReportsPanel` 傳下來;未勾分組列印時才有(REQ-20260901002) */
export interface ArDetailPrintHeaderInfo {
  reportLabel: string;
  customer: PartyInfo | null;
  from: string;
  to: string;
}

export function ArDetailItemTable({
  q,
  printHeader,
}: {
  q: {
    data: ArApDetailReport | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  /**
   * 有值就換上自行分頁的列印版面(REQ-20260901002):合計只印最後一頁、
   * 頁數有分母、每頁重印抬頭。沒值(勾了分組列印但查無資料時會走到)就維持原樣 ——
   * 那時螢幕這份仍然是唯一要印的東西,不能把它藏起來。
   */
  printHeader?: ArDetailPrintHeaderInfo | undefined;
}): JSX.Element {
  const allRows = q.data?.rows ?? [];
  /*
   * 已交貨 / 未交貨兩區(REQ-20260901008)。判斷用 `!== false` 而不是 `=== true`:
   * `delivered` 是**選填**欄位,只有 `ar-detail` 會帶 —— 沒帶的一律當成可請款,
   * 舊 client / 其他報表的行為才不會因為多了這個欄位而改變。
   */
  const rows = allRows.filter((r) => r.delivered !== false);
  const undeliveredRows = allRows.filter((r) => r.delivered === false);
  const items = toItemRows(rows);
  const undeliveredItems = toItemRows(undeliveredRows);

  // 金額欄的加總 = 各單 subtotal 的加總(未稅);折讓是單頭欄位,逐單加一次
  const totalAmount = items.reduce((sum, r) => sum + Number(r.line.amount), 0);
  const totalAllowance = rows.reduce((sum, r) => sum + Number(r.allowanceAmount ?? 0), 0);
  const footer: Array<string | null> = [
    '合計',
    null,
    null,
    null,
    null,
    null,
    formatMoney(totalAmount),
    formatMoney(totalAllowance),
    formatMoney(q.data?.totalSettled ?? '0'),
    formatMoney(q.data?.totalOutstanding ?? '0'),
  ];

  /*
   * 有標籤的合計說明區(REQ-20260901012)。**補在 `<tfoot>` 那排數字之外,不是取代它** ——
   * 數字直接落在各自欄位底下是有用的,使用者要的是「每一項是什麼、怎麼加起來的」。
   * 載入中 / 失敗 / 沒有已交貨的列時不渲染:那三態下的合計全是 0,擺出來只是雜訊。
   */
  const summary =
    q.isLoading || q.isError || items.length === 0 ? null : (
      <ArDetailTotalsSummary
        rows={rows}
        lines={items.map((r) => r.line)}
        totalSettled={q.data?.totalSettled ?? '0'}
        totalOutstanding={q.data?.totalOutstanding ?? '0'}
      />
    );

  /*
   * 未交貨區的小計。金額 / 折讓 / 已收款在這裡加(與上面的合計同一種算法),
   * 未收款用後端的 `undeliveredOutstanding` —— 那是「這些單還欠多少」的權威值,
   * 與上面的合計互斥(一張單只會落在其中一邊)。
   */
  const undeliveredFooter: Array<string | null> = [
    '小計',
    null,
    null,
    null,
    null,
    null,
    formatMoney(undeliveredItems.reduce((sum, r) => sum + Number(r.line.amount), 0)),
    formatMoney(undeliveredRows.reduce((sum, r) => sum + Number(r.allowanceAmount ?? 0), 0)),
    formatMoney(undeliveredRows.reduce((sum, r) => sum + Number(r.settledAmount), 0)),
    formatMoney(q.data?.undeliveredOutstanding ?? '0'),
  ];

  const table = (
    <ReportTable
      columns={arDetailColumns}
      rows={items}
      rowKey={(r) => r.line.key}
      /*
       * 十欄的表頭在 A4 上被擠成上下兩行(REQ-20260831008)。字級縮一級、拿掉
       * `<th>` 的 UA 粗體、一列不折 —— 三個都得用 `[&_th]:` 直接套到 th:
       * 粗體是 UA 設在 th 上的,`<tr>` 那層的 `text-sm` 也會蓋掉繼承來的字級。
       * `px-2` 收表頭的水平 padding,把「單行不折」撐開的寬度收回來。
       * 只有這張表傳,另外八張報表的表頭一個字都沒變。
       *
       * ⚠️ REQ-20260901002 之後**列印走的不是這張表**(是 `ArDetailPrintPages`,
       * 它自己把 td 也收成 `px-2`)。這裡留下的四個工具類現在只影響**螢幕**外觀,
       * 已經不是「印得下 A4」的機制 —— 要調列印寬度請改那一支。
       */
      headClassName="[&_th]:text-xs [&_th]:font-normal [&_th]:whitespace-nowrap [&_th]:px-2"
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint={EMPTY_HINT}
      footer={footer}
    />
  );

  /*
   * 未交貨區(REQ-20260901008)。整區 `print:hidden` —— 紙上那份是要拿去跟客人請款的,
   * 還沒交的貨印上去就是請了不該請的錢。一列都沒有時整區不渲染,不留一塊空標題。
   */
  const undelivered =
    undeliveredItems.length === 0 ? null : (
      <section
        className="mt-6 rounded-md bg-slate-50 p-3 print:hidden"
        data-testid="ar-detail-undelivered"
      >
        <h4 className="text-sm font-medium text-slate-700">未交貨（不列入本次請款）</h4>
        <p className="mt-0.5 mb-2 text-xs text-slate-500">
          以下商品尚未交貨，不列入本次請款。交貨完成後會自動移到上方的請款明細。
        </p>
        <ReportTable
          columns={undeliveredColumns}
          rows={undeliveredItems}
          rowKey={(r) => r.line.key}
          headClassName="[&_th]:text-xs [&_th]:font-normal [&_th]:whitespace-nowrap [&_th]:px-2"
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          emptyHint={EMPTY_HINT}
          footer={undeliveredFooter}
        />
      </section>
    );

  if (!printHeader) {
    return (
      <>
        {table}
        {summary}
        {undelivered}
      </>
    );
  }

  /*
   * 螢幕維持原本的 `ReportTable`(含 `<tfoot>` 合計列),列印換成 `ArDetailPrintPages`。
   * 兩份吃的是同一組 columns / items / footer,不是各算一份 ——
   * 而 `items` 已經只剩已交貨的列,列印那條路徑不需要再濾一次。
   */
  return (
    <>
      <div className="print:hidden" data-testid="ar-detail-screen-table">
        {table}
      </div>
      {/*
        螢幕的合計說明區。放在 `ar-detail-screen-table` **外面**是刻意的 ——
        那個 testid 是「螢幕上的那張表格」,測試會拿它 scope 查詢,
        把說明區塞進去會讓那些查詢連到表格以外的文字。紙上那份由 `summary` prop 走。
      */}
      <div className="print:hidden">{summary}</div>
      <ArDetailPrintPages
        columns={arDetailColumns}
        rows={items}
        rowKey={(r) => r.line.key}
        footer={footer}
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        emptyHint={EMPTY_HINT}
        // 日期收窄之後省下的左右空間全歸商品名稱(REQ-20260901003 第 5 點)
        growColumnKey="productName"
        // 只印在最後一頁,高度會被算進裝箱(REQ-20260901012)
        summary={summary}
        {...printHeader}
      />
      {undelivered}
    </>
  );
}

/**
 * 明細列 → 表身列(一張單有幾個品項就攤成幾列,第一列帶單頭欄位)。
 * `GroupedArDetailStatements` 逐客戶攤平時也用這一支(REQ-20260901013),所以 export。
 */
export function toItemRows(rows: ArApDetailRow[]): ItemRow[] {
  return rows.flatMap((row) =>
    printLines(row).map((line, i) => ({ row, line, isFirst: i === 0 })),
  );
}
